import { useEffect, useMemo, useState } from "react";
import { TrendingUp, Zap, Info } from "lucide-react";
import SegmentedToggle, { type SegmentOption } from "./SegmentedToggle";
import { cn } from "@/lib/utils";

export type PerfMode = "picks" | "parlays";

export type PicksStats = {
  wins: number;
  losses: number;
  pushes: number;
  units: number;
  roi: number;
  total_picks: number;
};

export type ParlayStats = {
  wins: number;
  losses: number;
  pushes: number;
  units: number;
  settled: number;
  avgOddsAmerican: number | null;
  biggestWinUnits: number;
};

interface Props {
  title: string;
  subtitle?: string;
  scope: "platform" | "user";
  picks: PicksStats;
  parlays: ParlayStats;
  emptyPicksHint?: string;
  emptyParlaysHint?: string;
}

const TABS: SegmentOption<PerfMode>[] = [
  { value: "picks", label: "Picks", icon: TrendingUp },
  { value: "parlays", label: "Parlays", icon: Zap },
];

function varianceTier(stats: ParlayStats): { label: string; tone: "muted" | "warn" | "danger" } {
  if (stats.settled === 0) return { label: "—", tone: "muted" };
  const a = stats.avgOddsAmerican ?? 0;
  if (a >= 500) return { label: "Extreme", tone: "danger" };
  if (a >= 200) return { label: "High", tone: "warn" };
  return { label: "Elevated", tone: "warn" };
}

function fmtAmerican(a: number | null): string {
  if (a === null || !Number.isFinite(a)) return "—";
  const rounded = Math.round(a);
  return rounded > 0 ? `+${rounded}` : `${rounded}`;
}

export default function PerformancePanel({
  title,
  subtitle,
  scope,
  picks,
  parlays,
  emptyPicksHint,
  emptyParlaysHint,
}: Props) {
  const [mode, setMode] = useState<PerfMode>("picks");
  // Re-trigger fade between tabs
  const [animKey, setAnimKey] = useState(0);
  useEffect(() => setAnimKey((k) => k + 1), [mode]);

  const tier = useMemo(() => varianceTier(parlays), [parlays]);

  const winRate = useMemo(() => {
    const decided = picks.wins + picks.losses;
    return decided > 0 ? Math.round((picks.wins / decided) * 100) : null;
  }, [picks]);

  return (
    <section className="te-card p-4 sm:p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="font-display text-xl sm:text-2xl font-bold">{title}</h2>
          {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
        </div>
        <SegmentedToggle
          options={TABS}
          value={mode}
          onChange={(v) => setMode(v as PerfMode)}
          ariaLabel={`${title} view`}
        />
      </div>

      <div key={animKey} className="mt-4 animate-fade-in">
        {mode === "picks" ? (
          picks.total_picks === 0 ? (
            <Empty>{emptyPicksHint ?? "No picks tracked yet"}</Empty>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <Cell
                label={scope === "user" ? "Your Record" : "Record"}
                value={`${picks.wins}-${picks.losses}-${picks.pushes}`}
              />
              <Cell
                label={scope === "user" ? "Your Units" : "Units"}
                value={`${picks.units > 0 ? "+" : ""}${picks.units.toFixed(1)}u`}
                accent
              />
              <Cell
                label={scope === "user" ? "Your ROI" : "ROI"}
                value={`${picks.roi > 0 ? "+" : ""}${picks.roi.toFixed(1)}%`}
                accent={picks.roi >= 0}
              />
              <Cell
                label={scope === "user" ? "Picks Tracked" : "Total Picks"}
                value={`${picks.total_picks}`}
              />
              {winRate !== null && (
                <Cell label="Win %" value={`${winRate}%`} className="col-span-2" />
              )}
            </div>
          )
        ) : parlays.settled === 0 ? (
          <>
            <Empty>{emptyParlaysHint ?? "No parlays settled yet"}</Empty>
            <VarianceNote />
          </>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3">
              <Cell
                label={scope === "user" ? "Your Parlay Record" : "Parlay Record"}
                value={`${parlays.wins}-${parlays.losses}${parlays.pushes ? `-${parlays.pushes}` : ""}`}
              />
              <Cell
                label={scope === "user" ? "Your Parlay Units" : "Units"}
                value={`${parlays.units > 0 ? "+" : ""}${parlays.units.toFixed(2)}u`}
                accent={parlays.units >= 0}
              />
              <Cell
                label={scope === "user" ? "Parlays Played" : "Parlays Settled"}
                value={`${parlays.settled}`}
              />
              <Cell label="Avg Odds" value={fmtAmerican(parlays.avgOddsAmerican)} />
              {parlays.biggestWinUnits > 0 && (
                <Cell
                  label="Biggest Win"
                  value={`+${parlays.biggestWinUnits.toFixed(2)}u`}
                  accent
                />
              )}
              <Cell
                label="Variance Tier"
                value={tier.label}
                className={cn(
                  tier.tone === "danger" && "ring-1 ring-danger/40",
                  tier.tone === "warn" && "ring-1 ring-accent/40",
                )}
              />
            </div>
            <VarianceNote />
          </>
        )}
      </div>
    </section>
  );
}

function Cell({
  label,
  value,
  accent,
  className,
}: {
  label: string;
  value: string;
  accent?: boolean;
  className?: string;
}) {
  return (
    <div className={cn("rounded-xl border border-border/70 bg-secondary/20 p-3", className)}>
      <div className="text-[10px] sm:text-xs uppercase text-muted-foreground tracking-wider">
        {label}
      </div>
      <div
        className={cn(
          "font-display text-xl sm:text-2xl font-bold mt-1 tabular-nums",
          accent && "te-grad-text",
        )}
      >
        {value}
      </div>
    </div>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-border/70 bg-secondary/20 px-4 py-6 text-sm text-muted-foreground">
      {children}
    </div>
  );
}

function VarianceNote() {
  return (
    <p className="mt-3 flex items-start gap-1.5 text-[11px] sm:text-xs text-muted-foreground italic leading-snug">
      <Info className="w-3.5 h-3.5 mt-0.5 shrink-0 text-accent" />
      Parlays are tracked separately due to higher variance — high-risk, entertainment-first.
    </p>
  );
}
